/* ── BRIEF 123 §3 — HOW A MEETING IS HELD, AS A PROPERTY OF THE EVENT ────────
 *
 * `EV_MEETING_KIND = { REU: "room", VIR: "virtual" }` (App.tsx:504@50fb417c,
 * until this brief) made two questions one:
 *
 *     is this event a meeting?          -> now the TYPE's `meta.isMeeting` (§1)
 *     is it held in person or online?   -> now the EVENT's `meetingMode` (§2)
 *
 * Collapsing them is what buried the Physical / Virtual choice the retired
 * Schedule Meeting dialog carried at the top of its own dialog inside a
 * fifteen-entry type dropdown, and what left a hearing-by-videoconference with
 * nowhere to live. 120 §3 was right that there must not be "a second switch that
 * means the same thing" and wrong about which switch it was: the type answers
 * WHAT KIND of event, the mode answers HOW IT IS HELD.
 *
 * `meetingMode` is a stored column (§2), nullable, no default. The default here
 * is a READ-side one, and it is why "person" rather than "": both modes send
 * invitations (RULED, Laurent 2026-08-23), so the only thing a mode decides is
 * whether a JOIN LINK is minted and a room may be booked — and defaulting a row
 * nobody has answered for to `online` would mint a Teams meeting for an event
 * whose author never asked for one. Physical is the conservative answer, and it
 * is the one the retired dialog opened on. */

export type MeetingMode = "person" | "online";

export const MEETING_MODES: readonly MeetingMode[] = ["person", "online"];

export const DEFAULT_MEETING_MODE: MeetingMode = "person";

/** The mode of a row that IS a meeting. Anything that is not the stored string
 *  `"online"` reads as Physical — including null, which is every row created
 *  before §2's column and every row whose type was flagged a meeting type after
 *  the fact. */
export function meetingModeOf(row: { meetingMode?: unknown } | null | undefined): MeetingMode {
  return row && row.meetingMode === "online" ? "online" : DEFAULT_MEETING_MODE;
}

/* ── BRIEF 124 §1 — A TYPE DECLARES A DEFAULT MODE ──────────────────────────
 *
 * Laurent, glancing #429: "a VIR virtual meeting still shows physical: one
 * should be able to link some types of meetings with physical or online by
 * default." It is 123's own correction — 123 moved the mode OUT of the type and
 * left the type's NAME still encoding it (`REU — Meeting in person`,
 * `VIR — Virtual meeting`). A type expresses a PREFERENCE; the event holds the
 * fact.
 *
 * [MEASURED] the complaint is about a NEW event, and the measurement is what
 * says so. `20260823180000_123_meeting_mode` backfilled BY ROLE — `inPerson` ->
 * 'person', `virtual` -> 'online' — so every VIR row that already existed
 * carries 'online' and displays Online correctly. What still showed Physical is
 * the event being CREATED: `form.meetingMode` is undefined until the toggle is
 * touched, and `meetingModeOf` above reads undefined as Physical.
 *
 * THREE STATES, IN THIS ORDER, AND THE ORDER IS THE WHOLE FUNCTION:
 *
 *   the row has answered      -> its own answer, always. A stored mode is a
 *                                fact about how a meeting IS held and a type's
 *                                preference may never overrule it.
 *   the row has not           -> the type's declared default.
 *   neither                   -> Physical, unchanged.
 *
 * The last line is 123's conservative default and the reasoning above still
 * stands: defaulting an unanswered row to `online` would mint a Teams meeting
 * for an event whose author never asked for one. A type that DECLARES `online`
 * is an author asking for one, which is exactly the difference.
 *
 * IT IS A DEFAULT AND NEVER A LOCK. The toggle stays editable on every row —
 * the whole point of 123 is that the mode belongs to the event.
 */
export function asMeetingMode(v: unknown): MeetingMode | null {
  return v === "person" || v === "online" ? v : null;
}

export function meetingModeFor(
  row: { meetingMode?: unknown } | null | undefined,
  typeDefault: MeetingMode | null | undefined,
): MeetingMode {
  const answered = asMeetingMode(row && row.meetingMode);
  if (answered) return answered;
  return typeDefault || DEFAULT_MEETING_MODE;
}

/** Is this event type one of the firm's meeting types? `codes` is
 *  `meetingTypeCodes(evtEntries)` (utils/taxonomyMap) — the fold, resolved once
 *  by the host and passed down, never re-derived per row. */
export function isMeetingType(
  typeCode: unknown,
  codes: ReadonlySet<string> | null | undefined,
): boolean {
  return !!codes && codes.has(String(typeCode ?? ""));
}

/* ── THE WORDS AN EVENT'S SAVE USES ─────────────────────────────────────────
 *
 * 123 §5 (Laurent, 2026-08-23) ruled this for TWO ACTS on THREE STATES, because
 * the Event modal then had two primaries and each needed a name and a toast.
 * **BRIEF 137 §4 REMOVED ONE OF THE ACTS**, so this is what remains: the ONE
 * save's label, and the toast that follows it.
 *
 *   state                   the save says      the toast says
 *   New event               Save the event     "Event created"
 *   Edit                    Save changes       "Event updated"
 *
 * NARROWED, NOT AMENDED (§4's own instruction): the header says what the
 * function answers today rather than describing a second column and then
 * retracting it. What went, and why each thing went WITH the button rather
 * than separately:
 *
 *   · `inviteKey` / `toastInviteKey` — there is one primary. 119 §2's second
 *     one is WITHDRAWN and its wording falls quiet (`modal.ag.save_invite`,
 *     `…save_send_invites{,_app}`, `…save_changes_send_invites{,_app}`,
 *     `…save_changes_update_invites`, the `toast.event.*_invites*` family).
 *     Their seed rows STAY (D28): a key with no call site costs nothing, and
 *     deleting rows is how a later caller silently ships English.
 *   · `params` — `{app}` appeared in the invite wording alone. Nothing the
 *     plain save says names a platform.
 *   · `hasMeeting` — it separated "Save changes & update invitations" from
 *     "Save changes & send invitations". With the invite half gone, both of
 *     its branches answered `modal.ag.save_changes` / `toast.event.updated`,
 *     so it was an input that could no longer change the answer. `isNew` is
 *     the whole of the state now.
 *   · `mode` — read only to decide whether `{app}` was named.
 *
 * AND `platformShortKey` WENT WITH THEM. It existed to say "Teams" rather than
 * "Microsoft Teams" inside an invite label ("Save & send Teams invitations"),
 * and `eventSaveWords` was its only caller in the tree. `platform.short.teams`
 * / `.zoom` / `.gmeet` stay seeded on the same D28 reasoning.
 *
 * KEYS, NEVER RESOLVED WORDS, and that survives the narrowing: each caller
 * holds its own `t`, which is what lets the BUTTON (`AgendaEventModal`, through
 * `saveLabel`) and the TOAST (`App.tsx`, after the persist) be read off one
 * derivation rather than composed twice and allowed to drift.
 */

export type EventSaveWords = {
  saveKey: string;
  toastPlainKey: string;
};

export function eventSaveWords(s: { isNew: boolean }): EventSaveWords {
  return s.isNew
    ? { saveKey: "modal.ag.save_event", toastPlainKey: "toast.event.created" }
    : { saveKey: "modal.ag.save_changes", toastPlainKey: "toast.event.updated" };
}
